import InboxTopBarContent from '@/components/inbox/InboxTopBarContent';
import { ContentWrapper } from '@/components/layout/ContentWrapper';
import MainLayout from '@/components/layout/MainLayout';
import { TABLE_REFRESH_TIMEOUT } from '@/config/constants';
import { PATH_INBOX_COMPLETED } from '@/config/paths';
import useAlerts from '@/hooks/useAlerts';
import useCases from '@/hooks/useCases';
import useUserPreferences from '@/hooks/useUserPreferences';
import useWindowSize from '@/hooks/useWindowSize';
import { getSubmittedCases } from '@/services/BackendAPI';
import { requestPedals } from '@/services/pedals';
import { ITableFilterOptions } from '@/types/ITableFilterOptions';
import { daDK } from '@mui/x-data-grid/locales';
import { collectFilterValues, mapReceivedCaseToTableCase } from '@/utils/tables';
import { setLocationOptions } from '@/utils/locationCache';
import { flexColumnFullHeight } from '@/utils/theme';
import { Box, Button } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { blue } from '@mui/material/colors';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  DataGridPro,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarQuickFilter,
  GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { FilterList } from '@mui/icons-material';
import { ChipInputValue } from '@/components/tables/ChipFilterInputValue';

const defaultFilterOptions: ITableFilterOptions = {
  location: [],
  specialty: [],
  doctor_initials: [],
  consultation_type: [],
  status: [],
  submitted_by: [],
};

export const HistoryPagev2 = observer(() => {
  const cases = useCases();
  const alerts = useAlerts();
  const userPreferences = useUserPreferences();
  const navigate = useNavigate();
  const size = useWindowSize();
  const [filterOptions, setFilterOptions] = useState<ITableFilterOptions>(defaultFilterOptions);
  const [loading, setLoading] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  const handleRowClick = async (caseId: number) => {
    await requestPedals();
    navigate(`/cases/${caseId}`);
  };

  const mappedCases = useMemo(() => {
    // Map cases data to table fields.
    return cases.completedCases?.map((c) => mapReceivedCaseToTableCase(c)) || [];
  }, [cases.completedCases]);

  useEffect(() => {
    // Collect values for filters when mappedCases changes.
    const options = collectFilterValues(mappedCases, defaultFilterOptions);
    setLocationOptions(options.location);
    setFilterOptions(options);
  }, [mappedCases]);

  const sortedAndFilteredCases = mappedCases;

  const windowHeightWithoutHeader = size.height - 80;

  const loadCases = (
    indicateLoading: boolean = true,
    startDate: Date | null,
    endDate: Date | null,
    query: string | null,
  ) => {
    if (indicateLoading) {
      setLoading(true);
    }
    getSubmittedCases(startDate, endDate, query)
      .then((results) => {
        cases.setCompletedCases(results);
      })
      .catch((e) => {
        console.error(e);
        alerts.error(
          'We cannot load the cases at the moment. Please try again or contact the administrator',
        );
      })
      .finally(() => {
        if (indicateLoading) {
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    userPreferences.setLastInboxPath(PATH_INBOX_COMPLETED);

    const startDate = userPreferences.completedCasesFilters?.from_date || null;
    const endDate = userPreferences.completedCasesFilters?.to_date || null;
    const query = userPreferences.completedCasesFilters?.query || null;

    // Load cases on page load and display loading indicator.
    loadCases(true, startDate, endDate, query);

    // Load cases regularly.
    const intervalId = setInterval(
      () => loadCases(false, startDate, endDate, query),
      TABLE_REFRESH_TIMEOUT,
    );
    return () => clearInterval(intervalId);
  }, [
    userPreferences.completedCasesFilters?.from_date,
    userPreferences.completedCasesFilters?.to_date,
    userPreferences.completedCasesFilters?.query,
  ]);

  const columns: GridColDef[] = [
    {
      field: 'created_at',
      type: 'date',
      display: 'flex',
      headerName: 'Dato',
      minWidth: 160,
      valueGetter: (value) => new Date(value),
      renderCell: (params) => {
        // show the number of days since the case was created, with the date as a tooltip
        const createdDate = new Date(params.value as number);
        const formattedDate = createdDate.toLocaleDateString('da-DK', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        });
        const distance = formatDistanceToNow(createdDate, {
          addSuffix: true,
        });

        return (
          <div>
            {formattedDate}
            <br />
            <small style={{ opacity: 0.5 }}>{distance}</small>
          </div>
        );
      },
    },
    { field: 'patient_id', headerName: 'Patient ID', width: 120 },
    { field: 'external_id', headerName: 'Journal ID', width: 120 },
    {
      field: 'doctor_initials',
      headerName: 'Læge',
      width: 140,
      type: 'singleSelect',
      valueOptions: filterOptions.doctor_initials,
      renderHeaderFilter: (params) => {
        return <ChipInputValue {...params} />;
      },
    },
    { field: 'flag_reason', headerName: 'Bemærkninger', width: 180 },
    {
      field: 'consultation_type',
      headerName: 'Konsultationstype',
      width: 180,
      type: 'singleSelect',
      valueOptions: filterOptions.consultation_type,
      renderHeaderFilter: (params) => {
        return <ChipInputValue {...params} />;
      },
    },
    {
      field: 'specialty',
      headerName: 'Speciale',
      width: 150,
      type: 'singleSelect',
      valueOptions: filterOptions.specialty,
      renderHeaderFilter: (params) => {
        return <ChipInputValue {...params} />;
      },
    },
    {
      field: 'location',
      headerName: 'Lokation',
      width: 150,
      type: 'singleSelect',
      valueOptions: filterOptions.location,
      renderHeaderFilter: (params) => {
        return <ChipInputValue {...params} />;
      },
    },
    {
      field: 'submitted_by',
      headerName: 'Indsendt af',
      width: 140,
      type: 'singleSelect',
      valueOptions: filterOptions.submitted_by,
      renderHeaderFilter: (params) => {
        return <ChipInputValue {...params} />;
      },
    },
    { field: 'id', headerName: 'ID', width: 90, type: 'string', description: 'Corti ID' },
  ];

  return (
    <MainLayout topBarContent={<InboxTopBarContent />}>
      <ContentWrapper
        wrapperSx={{
          ...flexColumnFullHeight,
          height: 'auto',
          width: '100vw',
          overflowX: 'overlay',
          maxHeight: windowHeightWithoutHeader,
        }}
        sx={flexColumnFullHeight}
      >
        <Box sx={{ overflowX: 'auto', width: '100%', minHeight: '100%' }}>
          <DataGridPro
            rows={sortedAndFilteredCases}
            sortModel={userPreferences.historySortModel}
            onSortModelChange={userPreferences.setHistorySortModel}
            filterModel={userPreferences.historyFiltersModel}
            onFilterModelChange={userPreferences.setHistoryFiltersModel}
            columns={columns}
            pagination
            pageSizeOptions={[100]}
            disableRowSelectionOnClick
            autosizeOnMount
            loading={loading}
            headerFilters={showFilters}
            slots={{
              headerFilterMenu: null,
              toolbar: QuickSearchToolbar,
            }}
            scrollbarSize={20}
            localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                setShowFilters,
                showFilters,
              } as GridToolbarProps & {
                setShowFilters: (show: boolean) => void;
                showFilters: boolean;
              },
            }}
            onRowClick={(params) => handleRowClick(params.row.id)}
            sx={{
              width: '100%',
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: blue[50],
                },
              },
              '& .datagrid--status--urgent': {
                backgroundColor: '#fff5f5',
                '&:hover': {
                  backgroundColor: '#ffe3e3',
                },
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'transparent',
              },
            }}
            getRowClassName={(params) =>
              `datagrid--status--${params.row.urgent ? 'urgent' : 'normal'}`
            }
            autosizeOptions={{
              includeOutliers: true,
              includeHeaders: true,
            }}
          />
        </Box>
      </ContentWrapper>
    </MainLayout>
  );
});

interface QuickSearchToolbarProps extends GridToolbarProps {
  setShowFilters: (show: boolean) => void;
  showFilters: boolean;
}

const QuickSearchToolbar = observer(
  ({
    setShowFilters,
    showFilters,
    showQuickFilter,
    quickFilterProps = {},
    csvOptions: _csvOptions,
    printOptions: _printOptions,
    ...containerProps
  }: QuickSearchToolbarProps) => {
    return (
      <Box
        {...containerProps}
        display="flex"
        justifyContent="space-between"
        sx={[{ p: 0.5, pb: 0 }, containerProps.sx] as SxProps<Theme>}
      >
        <Box display="flex" alignItems="center" gap={1} className="dd-privacy-allow">
          <GridToolbarColumnsButton />
          <Button
            onClick={() => setShowFilters(!showFilters)}
            color={showFilters ? 'secondary' : 'inherit'}
          >
            <FilterList />
            {showFilters ? 'Skjul filtre' : 'Vis filtre'}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {showQuickFilter !== false ? (
            <GridToolbarQuickFilter {...quickFilterProps} />
          ) : null}
        </Box>
      </Box>
    );
  },
);
